/* Movatic WMS — Frontend | CesarFilho 932048016 */
import { useEffect, useMemo, useState } from 'react'

export type SortDir = 'asc' | 'desc'

export type Column<T> = {
    key: keyof T | string
    header: string
    render?: (row: T) => React.ReactNode
    className?: string
    /** habilita click no cabeçalho para ordenar */
    sortable?: boolean
    /** campo de ordenação no servidor (ex.: product__code) */
    sortField?: string
    /** não quebrar linha */
    noWrap?: boolean
    /** alinhamento visual */
    align?: 'left' | 'center' | 'right'
}

type SortState = { key?: string; dir?: SortDir }

function cx(...parts: (string | false | undefined)[]) {
    return parts.filter(Boolean).join(' ')
}

type ColPersist = { order: string[]; hidden: string[] }
function loadPersist(id?: string): ColPersist | null {
    if (!id) return null
    try {
        const raw = localStorage.getItem(`dt:${id}`)
        return raw ? (JSON.parse(raw) as ColPersist) : null
    } catch { return null }
}
function savePersist(id: string, v: ColPersist) {
    try { localStorage.setItem(`dt:${id}`, JSON.stringify(v)) } catch { }
}

export default function DataTable<T extends Record<string, any>>({
    id,
    columns,
    data,
    loading,
    emptyLabel = 'Sem dados',
    enableColumnManager = true,
    initialHidden,
    sort,
    onSortChange,
}: {
    id?: string
    columns: Column<T>[]
    data: T[]
    loading?: boolean
    emptyLabel?: string
    enableColumnManager?: boolean
    initialHidden?: string[]
    sort?: SortState
    onSortChange?: (next: SortState) => void
}) {
    // ---------- persistência ordem/visibilidade ----------
    const persisted = loadPersist(id || '')
    const [order, setOrder] = useState<string[]>(
        persisted?.order ?? columns.map((c) => String(c.key)),
    )
    const [hidden, setHidden] = useState<string[]>(
        persisted?.hidden ?? initialHidden ?? [],
    )
    useEffect(() => { if (id) savePersist(id, { order, hidden }) }, [id, order, hidden])

    // se colunas mudam, sincroniza
    useEffect(() => {
        const keys = columns.map((c) => String(c.key))
        setOrder((prev) => {
            const merged = prev.filter((k) => keys.includes(k))
            keys.forEach((k) => { if (!merged.includes(k)) merged.push(k) })
            return merged
        })
        setHidden((prev) => prev.filter((k) => keys.includes(k)))
    }, [columns])

    // colunas na ordem + visíveis
    const orderedColumns = useMemo(() => {
        const byKey: Record<string, Column<T>> = {}
        columns.forEach((c) => (byKey[String(c.key)] = c))
        return order
            .map((k) => byKey[k])
            .filter((c): c is Column<T> => !!c && !hidden.includes(String(c.key)))
    }, [columns, order, hidden])

    // ---------- ordenação pelo cabeçalho ----------
    const currentSort = sort ?? {}
    const handleSortClick = (col: Column<T>) => {
        if (!col.sortable) return
        const key = String(col.key)
        // ciclo: (sem) -> desc (se align right) / asc (senão) -> asc/desc -> (sem)
        let next: SortState
        if (currentSort.key !== key) {
            const start: SortDir = col.align === 'right' ? 'desc' : 'asc'
            next = { key, dir: start }
        } else {
            if (currentSort.dir === 'asc') next = { key, dir: 'desc' }
            else if (currentSort.dir === 'desc') next = { key: undefined, dir: undefined }
            else next = { key, dir: col.align === 'right' ? 'desc' : 'asc' }
        }
        onSortChange?.(next)
    }

    // ---------- gerenciador de colunas (somente flags) ----------
    const [openMgr, setOpenMgr] = useState(false)
    const toggle = (k: string) =>
        setHidden((prev) => prev.includes(k) ? prev.filter((x) => x !== k) : [...prev, k])

    // ---------- cabeçalhos ----------
    const header = useMemo(
        () =>
            orderedColumns.map((c, i) => {
                const isSorted = currentSort.key === String(c.key) && currentSort.dir
                const arrow =
                    isSorted === 'asc' ? '▲' : isSorted === 'desc' ? '▼' : c.sortable ? '↕' : ''
                return (
                    <th
                        key={i}
                        onClick={() => handleSortClick(c)}
                        className={cx(
                            'text-left p-2 text-sm text-neutral-600 dark:text-neutral-300 select-none',
                            c.sortable && 'cursor-pointer hover:text-neutral-900 dark:hover:text-neutral-50',
                            c.align === 'right' && 'text-right',
                            c.align === 'center' && 'text-center',
                        )}
                        title={c.sortable ? 'Clique para ordenar' : undefined}
                    >
                        <span className={cx('inline-flex items-center gap-2', c.noWrap && 'whitespace-nowrap')}>
                            {c.header}
                            {arrow && <span className="text-xs opacity-70">{arrow}</span>}
                        </span>
                    </th>
                )
            }),
        [orderedColumns, currentSort],
    )

    return (
        <div className="overflow-hidden rounded-xl border border-neutral-200 dark:border-neutral-800">
            {enableColumnManager && (
                <div className="flex items-center justify-end p-2 border-b border-neutral-200 dark:border-neutral-800">
                    <button
                        className="btn subtle text-xs"
                        onClick={() => setOpenMgr((v) => !v)}
                        title="Exibir/ocultar colunas"
                    >⚙ Colunas</button>
                </div>
            )}

            {enableColumnManager && openMgr && (
                <div className="p-3 border-b border-neutral-200 dark:border-neutral-800 bg-neutral-50/60 dark:bg-neutral-900/40">
                    <div className="grid gap-1 sm:grid-cols-2 md:grid-cols-3">
                        {order.map((k) => {
                            const col = columns.find((c) => String(c.key) === k)
                            if (!col) return null
                            const isHidden = hidden.includes(k)
                            return (
                                <label
                                    key={k}
                                    className="flex items-center gap-2 rounded-md border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-950 px-2 py-1 text-sm"
                                >
                                    <input type="checkbox" checked={!isHidden} onChange={() => toggle(k)} />
                                    <span className="whitespace-nowrap">{col.header}</span>
                                </label>
                            )
                        })}
                    </div>
                </div>
            )}

            <div className="overflow-auto">
                <table className="min-w-full">
                    <thead className="bg-neutral-50 dark:bg-neutral-900/50">
                        <tr>{header}</tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td className="p-3 text-sm" colSpan={orderedColumns.length}>Carregando...</td></tr>
                        ) : data.length === 0 ? (
                            <tr><td className="p-3 text-sm" colSpan={orderedColumns.length}>{emptyLabel}</td></tr>
                        ) : (
                            data.map((row, idx) => (
                                <tr
                                    key={idx}
                                    className="odd:bg-white even:bg-neutral-50/50 dark:odd:bg-neutral-950 dark:even:bg-neutral-900/30
                             hover:bg-blue-50/70 dark:hover:bg-blue-900/20 transition-colors"
                                >
                                    {orderedColumns.map((c, i) => (
                                        <td
                                            key={i}
                                            className={cx(
                                                'p-2 text-sm',
                                                c.align === 'right' && 'text-right',
                                                c.align === 'center' && 'text-center',
                                                c.noWrap && 'whitespace-nowrap',
                                                c.className,
                                            )}
                                            title={c.noWrap && !c.render ? String(row[c.key as keyof T] ?? '') : undefined}
                                        >
                                            {c.render ? c.render(row) : String(row[c.key as keyof T] ?? '')}
                                        </td>
                                    ))}
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    )
}
